import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { PrimeIcons } from "primereact/api";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";
import EmailMarkupFormDialog from "./EmailMarkupFormDialog";

const EmailMarkupsView = () => {
  const [records, setRecords] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const message = useRef<any>();

  const fetchRecords = () => {
    setLoading(true);
    new BaseApiServiceImpl("/api/v1/email-markups")
      .getRequestWithJsonResponse({ offset, limit })
      .then(async (response) => {
        setRecords(response?.records || []);
        setTotalRecords(response?.totalRecords || 0);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  useEffect(() => {
    fetchRecords();
  }, [offset]);

  const openNewDialog = () => {
    setSelectedRecord(null);
    setIsDialogOpen(true);
  };

  const openEditDialog = (record: any) => {
    setSelectedRecord({
      id: record.id,
      countryId: record.countryId,
      pricePerEmail: record.pricePerEmail,
      currencyCode: record.currencyCode,
      freeTierEnabled: record.freeTierEnabled,
      freeTierLimit: record.freeTierLimit,
    });
    setIsDialogOpen(true);
  };

  const confirmDelete = (record: any) => {
    setRecordToDelete(record);
    setDeleteDialogVisible(true);
  };

  const handleDelete = () => {
    if (!recordToDelete?.id) return;
    setIsDeleting(true);
    new BaseApiServiceImpl("/api/v1/email-markups/" + recordToDelete.id)
      .deleteRequestWithJsonResponse({
        "Content-Type": "application/json",
        Authorization: "Bearer " + UserSessionUtils.getBearerToken(),
      })
      .then(() => {
        MessageUtils.showSuccessMessage(message, "Email markup deleted successfully");
        setDeleteDialogVisible(false);
        setRecordToDelete(null);
        fetchRecords();
      })
      .catch((error) => {
        MessageUtils.showErrorMessage(message, error.message);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleSaveSuccess = () => {
    setIsDialogOpen(false);
    setSelectedRecord(null);
    fetchRecords();
  };

  return (
    <div className="card">
      <div className="flex justify-content-between align-items-center mb-4">
        <h3>Email Markups & Price Per Email</h3>
        <Button label="Add New" icon={PrimeIcons.PLUS} onClick={openNewDialog} className="p-button-primary" />
      </div>
      <Messages ref={message} style={{ width: "100%" }} />
      <DataTable
        value={records}
        paginator
        rows={limit}
        totalRecords={totalRecords}
        lazy
        loading={loading}
        onPage={(e: any) => {
          setOffset((e.page ?? 0) * limit);
          setTotalRecords(e.total);
        }}
        responsiveLayout="scroll"
      >
        <Column field="countryName" header="Country" sortable />
        <Column field="pricePerEmail" header="Price Per Email" sortable />
        <Column field="currencyCode" header="Currency" />
        <Column field="freeTierEnabled" header="Free Tier Enabled" body={(rowData) => (rowData.freeTierEnabled ? "Yes" : "No")} />
        <Column field="freeTierLimit" header="Free Tier Limit" />
        <Column
          header="Actions"
          body={(rowData) => (
            <div className="flex gap-2">
              <Button icon={PrimeIcons.PENCIL} className="p-button-text p-button-sm" onClick={() => openEditDialog(rowData)} />
              <Button icon={PrimeIcons.TRASH} className="p-button-text p-button-danger p-button-sm" onClick={() => confirmDelete(rowData)} />
            </div>
          )}
        />
      </DataTable>

      <EmailMarkupFormDialog
        isOpen={isDialogOpen}
        toggle={() => setIsDialogOpen(false)}
        record={selectedRecord}
        reloadFn={handleSaveSuccess}
      />

      <Dialog
        header="Delete Email Markup"
        visible={deleteDialogVisible}
        style={{ width: "30vw" }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon={PrimeIcons.TIMES} onClick={() => setDeleteDialogVisible(false)} className="p-button-text" />
            <Button label="Delete" icon={PrimeIcons.CHECK} onClick={handleDelete} loading={isDeleting} className="p-button-danger" />
          </div>
        }
      >
        <p>
          Are you sure you want to delete the email markup for <b>{recordToDelete?.countryName}</b>?
          This action cannot be undone.
        </p>
      </Dialog>
    </div>
  );
};

export default EmailMarkupsView;
